import { getHttpClientErrorMessage, getHttpServerErrorMessage } from '@/utils'
import { endpoints } from './endpoints'
import { TypeLang } from '@/schema'

// Define specific types for different content types
type JsonValue = string | number | boolean | null | JsonObject | JsonArray
type JsonObject = { [key: string]: JsonValue }
type JsonArray = JsonValue[]

// More specific header types
type AcceptHeader = 'application/json' | 'application/pdf' | 'application/xml' | 'application/csv' | 'application/vnd.ms-excel' | 'application/zip' | 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
type ContentTypeHeader = 'application/json' | 'application/x-www-form-urlencoded' | 'multipart/form-data' | 'text/plain'

type TypeFetchOptions = {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE'
  mode?: 'cors' | 'no-cors' | 'same-origin'
  cache?: 'default' | 'no-cache' | 'no-store' | 'reload' | 'force-cache' | 'only-if-cached'
  credentials?: 'same-origin' | 'include' | 'omit'
  headers?: Record<string, string | undefined> & {
    'Accept'?: AcceptHeader
    'Content-Type'?: ContentTypeHeader
    'Authorization'?: string
    'DY-Access-Token'?: string
    'DY-Lang'?: string
  }
  redirect?: 'follow' | 'manual' | 'error'
  referrerPolicy?: 'no-referrer' | 'no-referrer-when-downgrade' | 'origin' | 'origin-when-cross-origin' | 'same-origin' | 'strict-origin' | 'strict-origin-when-cross-origin' | 'unsafe-url'
  body?: BodyInit | null
  signal?: AbortSignal
  next?: {
    revalidate?: number
    tags?: string[]
  }
}
// Any body that you want to add to your request: this can be a Blob, an ArrayBuffer, a TypedArray, a DataView, a FormData, a URLSearchParams, string object or literal

const defaultOptions:TypeFetchOptions = {
  method: 'GET',
  mode: 'cors',
  cache: 'default',
  credentials: 'same-origin',
  headers: {
    'Accept': 'application/json',
    'Content-Type': 'application/json'
  },
  redirect: 'follow',
  referrerPolicy: 'no-referrer',
  body: null
}

type TypeQuery = {
  lang?: TypeLang | string
  slug?: string
  category_slug?: string
  content_slug?: string
  ids?: string
  searchParams?: {
    q?: string
    sort?: string
    direction?: 'asc' | 'desc'
    page?: number
    limit?: number
    status?: string,
    date_start?: string
    mode?: string
  }
}


type TypeFetchApi = (urlNamespace: keyof typeof endpoints, query?: TypeQuery, options?:TypeFetchOptions) => Promise<Response | Blob>

export const fetchAPI:TypeFetchApi = async (endpointNamespace, query = {}, fetchOptions) => {
  if(!endpointNamespace || !endpoints[endpointNamespace]) {
    throw new Error(`api/endpoints.ts -> No endpoint with namespace: '${endpointNamespace}'`)
  }

  const url = endpoints[endpointNamespace](query)
  const options = !fetchOptions ? defaultOptions : {...defaultOptions, ...fetchOptions }
  const body = options.body ?? null
  // console.log('🟡🟡 endpoint:', url)

  const res:Response = await fetch(url, {...options, body })

  switch (res.status) {
    case 204:
    case 205:
      // those status do not return a body
      return res
    case 200:
    case 201:
      if (typeof window === 'undefined') return res
      if (endpointNamespace.indexOf('download') !== -1) {
        const file = await res.blob()
        return file
      }
      const data = await res.json()
      return Response.json(data)
    case 400:
    case 401:
    case 402:
    case 403:
    case 404:
    case 405:
    case 406:
    case 407:
    case 408:
    case 409:
    case 410:
    case 411:
    case 412:
    case 413:
    case 414:
    case 415:
    case 416:
    case 417:
    case 421:
    case 422:
      console.log(`❌ DY '${endpointNamespace}' | ${res.status} - ${getHttpClientErrorMessage(res.status)} ${query.slug && `[slug: ${query.slug}]`}`)
      return res
    case 500:
    case 501:
    case 502:
    case 503:
    case 504:
    case 505:
    case 506:
    case 507:
    case 508:
    case 509:
    case 510:
    case 511:
      throw new Error(`❌ DY '${endpointNamespace}' | ${res.status} - ${getHttpServerErrorMessage(res.status)} ${query.slug && `[slug: ${query.slug}]`}`)
    default:
      console.log('res.status', res.status)
      throw new Error('❌ DY Unhandled 😭 | Something went wrong with the fetch')
  }
}
